import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Loader2, Sparkles, X, Check, AlertCircle, Wand2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cap full-visit recordings at 5 minutes. Longer than a typical
// dictation and well within Whisper's 25MB file limit at webm/opus.
const MAX_FULL_VISIT_MS = 5 * 60 * 1000;

// Sections that the LLM can fill. The mapping doubles as the order they
// appear in the confirm modal and as the canonical save list.
const FULL_VISIT_SECTIONS = [
  { key: 'chief_complaints', label: 'Chief Complaints', accent: 'amber' },
  { key: 'past_history', label: 'Past History', accent: 'slate' },
  { key: 'clinical_examination', label: 'Clinical Examination', accent: 'slate' },
  { key: 'provisional_diagnosis', label: 'Provisional Diagnosis', accent: 'amber' },
  { key: 'final_diagnosis', label: 'Final Diagnosis', accent: 'amber' },
  { key: 'treatment_plan', label: 'Treatment Plan', accent: 'emerald' },
  { key: 'visit_notes', label: 'Visit Notes', accent: 'slate' },
];

const ACCENT_MAP = {
  amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' },
};

/**
 * FullVisitDictationButton — single voice button that captures an entire
 * consultation, then distributes the content into every section.
 *
 * Click → mic recording starts (with Web Speech API live preview)
 * Click again (or 5min auto-stop) → audio → Whisper → Claude full-visit parser
 *                                  → confirm modal → onConfirm distributes.
 */
export default function FullVisitDictationButton({
  inventoryHint = [],
  onConfirm,   // (payload) => void  payload = { sectionsToSave: {key: content}, medicines, tests, transcript }
  disabled = false,
}) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [livePartial, setLivePartial] = useState('');
  const [payload, setPayload] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const autoStopRef = useRef(null);
  const speechRecRef = useRef(null);

  // ---------------- Cleanup ----------------
  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (speechRecRef.current) {
      try { speechRecRef.current.stop(); } catch (_) {}
      speechRecRef.current = null;
    }
    recorderRef.current = null;
  }, []);
  useEffect(() => () => cleanup(), [cleanup]);

  // ---------------- Live Web Speech API preview ----------------
  // Runs alongside MediaRecorder. Browser does on-device recognition while
  // Whisper takes the final pass — gives the doctor immediate confidence
  // that audio is being picked up. Falls back to recording-only if the
  // browser doesn't support webkitSpeechRecognition (Firefox).
  const startLivePreview = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = 'en-IN';
      rec.onresult = (e) => {
        let live = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          live += e.results[i][0].transcript;
        }
        setLivePartial((prev) => (prev + ' ' + live).trim().slice(-400));
      };
      rec.onerror = () => { /* swallow — preview is best-effort */ };
      rec.start();
      speechRecRef.current = rec;
    } catch (_) { /* not available */ }
  }, []);

  // ---------------- Recording lifecycle ----------------
  const startRecording = async () => {
    setElapsed(0); setLivePartial(''); chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;

      let mime = 'audio/webm;codecs=opus';
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = 'audio/webm';
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        setRecording(false);
        if (blob.size < 1000) {
          toast.info('Recording was too short. Speak for at least a few seconds.');
          return;
        }
        await sendForParse(blob, mime);
      };

      recorder.start(250);
      setRecording(true);
      startLivePreview();

      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      autoStopRef.current = setTimeout(() => {
        if (recorderRef.current?.state === 'recording') {
          recorderRef.current.stop();
          toast.info('Auto-stopped at 5 minutes.');
        }
      }, MAX_FULL_VISIT_MS);
    } catch (err) {
      console.error('Mic error:', err);
      toast.error('Microphone access denied. Please allow microphone in browser settings.');
      cleanup();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  };

  // ---------------- Transcribe → Parse ----------------
  const sendForParse = async (blob, mime) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append('audio', blob, mime.includes('mp4') ? 'visit.mp4' : 'visit.webm');
      const tr = await axios.post(`${API_URL}/api/transcribe`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const transcript = (tr.data?.text || '').trim();
      if (!transcript) {
        toast.warning('Could not detect any speech. Please try again.');
        return;
      }
      const parseRes = await axios.post(`${API_URL}/api/voice/parse-full-visit`, {
        transcript,
        inventory_hint: inventoryHint?.slice(0, 100),
      }, { withCredentials: true });
      setPayload(parseRes.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Voice processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const onClick = () => {
    if (processing) return;
    if (recording) stopRecording();
    else startRecording();
  };

  // Format mm:ss
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');

  return (
    <>
      <Button
        size="sm"
        onClick={onClick}
        disabled={disabled || processing}
        className={`gap-2 h-9 px-4 transition shadow-md ${
          recording
            ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
            : processing
              ? 'bg-iith-navyMid text-white'
              : 'bg-gradient-to-r from-iith-navy via-iith-navyMid to-iith-saffron hover:opacity-95 text-white'
        }`}
        data-testid="full-visit-dictation-btn"
        title="Dictate the entire visit — AI will distribute content into sections"
      >
        {processing ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Transcribing visit…</span>
          </>
        ) : recording ? (
          <>
            <Square className="h-4 w-4 fill-current" />
            <span>Stop {mm}:{ss}</span>
          </>
        ) : (
          <>
            <Wand2 className="h-4 w-4" />
            <span>Dictate Full Visit</span>
            <Badge variant="outline" className="ml-1 text-[9px] bg-white/20 text-white border-white/30">AI</Badge>
          </>
        )}
      </Button>

      {/* Live partial transcript bubble */}
      {recording && (livePartial || elapsed > 0) && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] max-w-2xl w-[calc(100%-3rem)] bg-slate-900/95 backdrop-blur-md text-white rounded-2xl shadow-2xl px-5 py-3 border border-white/10"
          data-testid="full-visit-live-partial"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">
              Listening · {mm}:{ss} / 05:00
            </span>
            <button
              onClick={stopRecording}
              className="ml-auto text-[10px] underline text-white/70 hover:text-white"
              data-testid="full-visit-stop-link"
            >
              Stop & process
            </button>
          </div>
          <p className="text-sm leading-snug text-white/95 line-clamp-3">
            {livePartial || <span className="italic text-white/40">Start speaking… your words will appear here in real time.</span>}
          </p>
        </div>
      )}

      <FullVisitConfirmModal
        open={!!payload}
        payload={payload}
        onClose={() => setPayload(null)}
        onConfirm={(p) => {
          onConfirm?.(p);
          setPayload(null);
        }}
      />
    </>
  );
}

// ===================== Confirm Modal =====================

function FullVisitConfirmModal({ open, payload, onClose, onConfirm }) {
  // Per-section opt-in. Default ON for any section with content, OFF for empty.
  const [selected, setSelected] = useState({});
  const [editedContent, setEditedContent] = useState({});

  useEffect(() => {
    if (!open || !payload) return;
    const next = {};
    const edits = {};
    for (const s of FULL_VISIT_SECTIONS) {
      const content = payload[s.key] || '';
      next[s.key] = !!content.trim();
      edits[s.key] = content;
    }
    next.medicines = (payload.medicines || []).length > 0;
    next.tests = (payload.tests || []).length > 0;
    setSelected(next);
    setEditedContent(edits);
  }, [open, payload]);

  if (!open || !payload) return null;

  const handleConfirm = () => {
    // Build the sections payload — only include sections the doctor ticked.
    const sectionsToSave = {};
    for (const s of FULL_VISIT_SECTIONS) {
      if (selected[s.key] && editedContent[s.key]?.trim()) {
        sectionsToSave[s.key] = editedContent[s.key];
      }
    }
    // Only ship master-matched items downstream — unmatched ones can't
    // be dispensed/ordered, so the doctor must add them manually via the
    // section's own search if needed.
    const matchedMeds = (payload.medicines || []).filter((m) => m && m.master_match);
    const matchedTests = (payload.tests || []).filter((t) => t && t.master_match);
    onConfirm({
      sectionsToSave,
      medicines: selected.medicines ? matchedMeds : [],
      tests: selected.tests ? matchedTests : [],
      transcript: payload.transcript || '',
    });
  };

  const matchedMedsCount = (payload.medicines || []).filter((m) => m && m.master_match).length;
  const unmatchedMedsCount = (payload.medicines || []).length - matchedMedsCount;
  const matchedTestsCount = (payload.tests || []).filter((t) => t && t.master_match).length;
  const unmatchedTestsCount = (payload.tests || []).length - matchedTestsCount;

  const totalSelected =
    FULL_VISIT_SECTIONS.filter(s => selected[s.key] && editedContent[s.key]?.trim()).length
    + (selected.medicines && matchedMedsCount > 0 ? 1 : 0)
    + (selected.tests && matchedTestsCount > 0 ? 1 : 0);

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden border-0 flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="full-visit-confirm-modal"
      >
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-iith-navy via-iith-navyMid to-[#0A1F38] text-white px-7 pt-5 pb-4 flex-shrink-0">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-iith-saffron/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-iith-gold/15 rounded-full blur-3xl" />
          <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center">
                <Wand2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>
                  Visit Dictation Captured
                </h3>
                <p className="text-[11px] text-white/75">
                  Review what AI heard. Untick anything you don't want to save. Edit inline.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="full-visit-confirm-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Transcript */}
        <div className="px-7 py-3 bg-slate-50 border-b flex-shrink-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Transcript</p>
          <p className="text-xs text-slate-700 italic leading-snug line-clamp-3" data-testid="full-visit-transcript">
            "{payload.transcript || '—'}"
          </p>
        </div>

        {/* Section list */}
        <ScrollArea className="flex-1 bg-slate-50/40 min-h-0">
          <div className="px-7 py-4 space-y-2">
            {FULL_VISIT_SECTIONS.map((s) => {
              const content = editedContent[s.key] || '';
              const hasContent = !!content.trim();
              return (
                <SectionEditableRow
                  key={s.key}
                  section={s}
                  content={content}
                  hasContent={hasContent}
                  checked={!!selected[s.key]}
                  onChangeChecked={(v) => setSelected((p) => ({ ...p, [s.key]: v }))}
                  onChangeContent={(v) => setEditedContent((p) => ({ ...p, [s.key]: v }))}
                />
              );
            })}

            {/* Prescription summary row */}
            {(payload.medicines || []).length > 0 && (
              <PreviewListRow
                checked={!!selected.medicines}
                onChange={(v) => setSelected((p) => ({ ...p, medicines: v }))}
                label="Prescription (Medicines)"
                count={matchedMedsCount}
                unmatchedCount={unmatchedMedsCount}
                accent="blue"
                preview={(payload.medicines || []).map((m) => {
                  const name = (m && m.master_match && m.master_match.name) || m?.medicine_name || '';
                  return `${m && m.master_match ? '' : '⚠ '}${name}${m?.dosage ? ' ' + m.dosage : ''}`;
                }).slice(0, 4).join(' · ') + ((payload.medicines || []).length > 4 ? ` · +${payload.medicines.length - 4} more` : '')}
                testId="full-visit-toggle-medicines"
              />
            )}
            {(payload.tests || []).length > 0 && (
              <PreviewListRow
                checked={!!selected.tests}
                onChange={(v) => setSelected((p) => ({ ...p, tests: v }))}
                label="Investigations"
                count={matchedTestsCount}
                unmatchedCount={unmatchedTestsCount}
                accent="purple"
                preview={(payload.tests || []).map((t) => {
                  const name = (t && t.master_match && t.master_match.name) || t?.name || (typeof t === 'string' ? t : '');
                  return `${t && t.master_match ? '' : '⚠ '}${name}`;
                }).slice(0, 6).join(' · ')}
                testId="full-visit-toggle-tests"
              />
            )}

            {totalSelected === 0 && (
              <div className="text-center py-8 text-slate-400">
                <AlertCircle className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm italic">Nothing captured. Try dictating again.</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="px-7 py-3 border-t flex items-center justify-between bg-white flex-shrink-0">
          <Button variant="ghost" onClick={onClose} className="gap-1.5 text-slate-600" data-testid="full-visit-cancel-btn">
            <X className="h-4 w-4" /> Discard
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500" data-testid="full-visit-selection-count">
              {totalSelected} section{totalSelected !== 1 ? 's' : ''} selected
            </span>
            <Button
              onClick={handleConfirm}
              disabled={totalSelected === 0}
              className="bg-gradient-to-r from-iith-navy to-iith-saffron hover:opacity-95 text-white gap-1.5 px-5"
              data-testid="full-visit-apply-btn"
            >
              <Check className="h-4 w-4" />
              Apply to Visit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionEditableRow({ section, content, hasContent, checked, onChangeChecked, onChangeContent }) {
  const cls = ACCENT_MAP[section.accent] || ACCENT_MAP.slate;
  const empty = !hasContent;
  return (
    <div
      className={`bg-white border rounded-lg shadow-sm transition ${
        checked && !empty ? 'border-iith-saffron/40' : 'border-slate-200'
      } ${empty ? 'opacity-60' : ''}`}
      data-testid={`full-visit-row-${section.key}`}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            checked={checked && !empty}
            disabled={empty}
            onCheckedChange={(v) => !empty && onChangeChecked(v === true)}
            data-testid={`full-visit-check-${section.key}`}
          />
          <span className="text-sm font-bold text-slate-900">{section.label}</span>
        </label>
        {empty ? (
          <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-400 border-slate-200 italic">
            Not dictated
          </Badge>
        ) : checked ? (
          <Badge variant="outline" className="text-[9px] gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold">
            <Check className="h-2.5 w-2.5" /> Will save
          </Badge>
        ) : (
          <span className="text-[10px] text-slate-400 italic">Skipped</span>
        )}
      </div>
      {!empty && (
        <div className="px-3 py-2">
          <div
            className={`text-xs text-slate-700 leading-snug rounded p-2 ${cls.bg} ${cls.border} border max-h-32 overflow-y-auto`}
            dangerouslySetInnerHTML={{ __html: content }}
            data-testid={`full-visit-content-${section.key}`}
          />
        </div>
      )}
    </div>
  );
}

function PreviewListRow({ checked, onChange, label, count, unmatchedCount = 0, accent, preview, testId }) {
  const accentMap = {
    blue: { bg: 'bg-iith-mist', text: 'text-iith-navy', border: 'border-iith-navy/20' },
    purple: { bg: 'bg-[#FFF7F3]', text: 'text-iith-saffron', border: 'border-iith-saffron/30' },
  };
  const cls = accentMap[accent] || accentMap.blue;
  const disabled = count === 0;
  return (
    <div
      className={`bg-white border rounded-lg p-3 shadow-sm transition ${
        checked && !disabled ? 'border-iith-saffron/40' : 'border-slate-200'
      } ${disabled ? 'opacity-70' : ''}`}
      data-testid={`full-visit-listrow-${testId}`}
    >
      <label className={`flex items-start gap-2.5 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <Checkbox
          checked={checked && !disabled}
          disabled={disabled}
          onCheckedChange={(v) => !disabled && onChange(v === true)}
          className="mt-1"
          data-testid={testId}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-sm font-bold text-slate-900">{label}</p>
              <Badge variant="outline" className={`text-[9px] h-4 px-1.5 ${cls.bg} ${cls.text} ${cls.border}`}>
                {count} matched
              </Badge>
              {unmatchedCount > 0 && (
                <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-amber-50 text-amber-700 border-amber-200" title="These items couldn't be reconciled against your master catalogue and won't be added. Add them manually from the section.">
                  ⚠ {unmatchedCount} skipped
                </Badge>
              )}
            </div>
            {disabled ? (
              <span className="text-[10px] text-slate-400 italic">No master match</span>
            ) : checked ? (
              <Badge variant="outline" className="text-[9px] gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 font-semibold">
                <Check className="h-2.5 w-2.5" /> Will save
              </Badge>
            ) : (
              <span className="text-[10px] text-slate-400 italic">Skipped</span>
            )}
          </div>
          {preview && (
            <p className={`text-xs mt-1 line-clamp-2 ${checked ? 'text-slate-700' : 'text-slate-400'}`}>
              {preview}
            </p>
          )}
        </div>
      </label>
    </div>
  );
}
